// src/services/DeviceApiService.ts
import axios, { AxiosResponse } from "axios";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "@/core/services/FirebaseService";

/**
 * Resolves once Firebase has finished restoring the persisted auth session.
 * `auth.currentUser` is null synchronously on first load (the session is
 * restored asynchronously), so any "am I signed in?" check made on mount can
 * race and wrongly conclude the user is logged out. Memoized so it only waits
 * once per app lifetime.
 */
let _authReady: Promise<void> | null = null;
function authReady(): Promise<void> {
  if (!_authReady) {
    _authReady = new Promise<void>((resolve) => {
      const unsub = onAuthStateChanged(auth, () => {
        unsub();
        resolve();
      });
    });
  }
  return _authReady;
}

// API Base URL
const API_BASE_URL = "https://markaswalet-iot.techiro.co.id/api/camera";

// Interface definitions
export interface Device {
  id: number;
  name: string | null;
  location: string | null;
  city?: string | null;
  province?: string | null;
  stream_key: string;
  vendor?: "rtmp_push" | "tuya_cloud";
  tuya_device_id?: string | null;
  owner_uid?: string | null;
  owner_email?: string | null;
  owner_name?: string | null;
  shares?: Array<{ uid: string; email: string | null; name: string | null }>;
  created_at: string;
  updated_at: string;
  status?: "online" | "offline" | "unknown";
  // AI counter overlay on/off. When false the camera streams directly from Tuya
  // (no Contabo CV pipeline). Default true. Only meaningful for Tuya cameras.
  cv_enabled?: boolean;
  // Master connect/disconnect. When false the camera does NOT stream at all
  // (no Tuya allocate calls) — saves Tuya quota. Default true.
  stream_enabled?: boolean;
}

// A user-submitted counting-quality report for an IoT camera.
export interface IotReport {
  id: number;
  device_id: number;
  device: { id: number; name: string | null; location: string | null } | null;
  uid: string;
  email: string;
  reporter_name: string;
  category: "akurasi" | "koneksi" | "pemasangan" | "lainnya";
  aspects: string[];
  description: string;
  attachments: string[];
  status: "baru" | "diproses" | "selesai" | "ditolak";
  admin_note: string | null;
  handled_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface RegisterDeviceRequest {
  name?: string;
  location?: string;
  password: string;
}

export interface RegisterDeviceResponse {
  id: number;
  stream_key: string;
}

export interface UpdateStreamRequest {
  id: number;
  password: string;
}

export interface UpdateStreamResponse {
  stream_key: string;
}

export interface GetDeviceDataRequest {
  id: number;
  password: string;
}

export interface GetDeviceDataResponse {
  id: number;
  name: string | null;
  location: string | null;
  stream_key: string;
}

export interface ListDevicesRequest {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: "id" | "name" | "location" | "created_at" | "updated_at";
  sort_order?: "asc" | "desc";
}

export interface ListDevicesResponse {
  data: Device[];
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
    from: number | null;
    to: number | null;
  };
}

export interface DeviceStatsResponse {
  total_devices: number;
  devices_with_name: number;
  devices_with_location: number;
  recent_devices: number;
  oldest_device: string | null;
  newest_device: string | null;
}

export interface UpdateDeviceRequest {
  id: number;
  password?: string;
  name?: string;
  location?: string;
  city?: string;
  province?: string;
}

export interface BirdStatsDailyCount {
  date: string;
  count_in: number;
  count_out: number;
}

export interface BirdStatsHourlyDist {
  hour: number;
  avg_count: number;
}

export interface BirdStatsSeriesPoint {
  label: string;
  in: number;
  out: number;
}
export interface BirdStatsHourlyPoint {
  hour: number;
  in: number;
  out: number;
}

export interface BirdStatsDevice {
  device_id: number;
  device_name: string | null;
  location: string | null;
  city: string | null;
  province: string | null;
  status: string;
  updated_at: string;
  last_recorded_at: string | null;
  total_in: number;
  total_out: number;
  total_crossings: number;
  peak_hour: number | null;
  crossing_count: number;
  series: BirdStatsSeriesPoint[];
  hourly: BirdStatsHourlyPoint[];
  daily_counts: BirdStatsDailyCount[];
  hourly_distribution: BirdStatsHourlyDist[];
}

export interface BirdStatsResponse {
  data: BirdStatsDevice[];
  summary: {
    total_in: number;
    total_out: number;
    total_crossings: number;
    net_flow: number;
    active_devices: number;
    device_count: number;
    avg_per_active: number;
    peak_hour: number | null;
    growth_pct: number;
    top_location: string;
    top_location_count: number;
    series: Array<{ label: string; in: number; out: number; crossings: number }>;
    time_range: string;
    start_date: string;
    // legacy
    total_birds: number;
  };
}

export interface ResetAllPasswordsRequest {
  new_password?: string;
  confirm_reset: boolean;
}

export interface ResetAllPasswordsResponse {
  message: string;
  affected_devices: number;
  new_password: string;
  reset_time: string;
}

export interface ResetDevicePasswordRequest {
  device_id: number;
  new_password?: string;
}

export interface ResetDevicePasswordResponse {
  message: string;
  device: {
    id: number;
    name: string | null;
    location: string | null;
  };
  new_password: string;
  reset_time: string;
}

export interface ApiErrorResponse {
  error: string;
}

export interface DeleteDeviceRequest {
  id: number;
  password?: string;
}

// Custom error class for API errors
export class DeviceApiError extends Error {
  public status: number;
  public response?: any;

  constructor(message: string, status: number, response?: any) {
    super(message);
    this.name = "DeviceApiError";
    this.status = status;
    this.response = response;
  }
}

// API Service class
class DeviceApiService {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;

    // Attach the signed-in user's Firebase ID token to IoT API requests only
    // (scoped by URL so other axios calls in the app are unaffected).
    axios.interceptors.request.use(async (config) => {
      if (typeof config.url === "string" && config.url.startsWith(API_BASE_URL)) {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          config.headers = config.headers || {};
          (config.headers as Record<string, string>).Authorization =
            `Bearer ${token}`;
        }
      }
      return config;
    });

    // Setup axios interceptors for better error handling
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          // Server responded with error status
          const status = error.response.status;
          const data = error.response.data;
          const message =
            data?.error || error.message || "Terjadi kesalahan pada server";

          throw new DeviceApiError(message, status, data);
        } else if (error.request) {
          // Request was made but no response received
          throw new DeviceApiError("Tidak dapat terhubung ke server", 0);
        } else {
          // Something else happened
          throw new DeviceApiError(error.message || "Terjadi kesalahan", 0);
        }
      }
    );
  }

  /**
   * Register a new device
   */
  async registerDevice(
    data: RegisterDeviceRequest
  ): Promise<RegisterDeviceResponse> {
    try {
      const response: AxiosResponse<RegisterDeviceResponse> = await axios.post(
        `${this.baseURL}/regist`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          timeout: 10000,
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error registering device:", error);
      throw error;
    }
  }

  /**
   * Update stream key for a device
   */
  async updateStreamKey(
    data: UpdateStreamRequest
  ): Promise<UpdateStreamResponse> {
    try {
      const response: AxiosResponse<UpdateStreamResponse> = await axios.put(
        `${this.baseURL}/update-stream`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          timeout: 10000,
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error updating stream key:", error);
      throw error;
    }
  }

  /**
   * Get device data by ID and password
   */
  async getDeviceData(
    data: GetDeviceDataRequest
  ): Promise<GetDeviceDataResponse> {
    try {
      const response: AxiosResponse<GetDeviceDataResponse> = await axios.get(
        `${this.baseURL}/get-device-data`,
        {
          params: data,
          headers: {
            Accept: "application/json",
          },
          timeout: 10000,
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error getting device data:", error);
      throw error;
    }
  }

  /**
   * List devices. Owner sees own devices; admins see all (enforced server-side
   * via the Firebase ID token attached by the request interceptor).
   */
  async listDevices(
    params: ListDevicesRequest = {}
  ): Promise<ListDevicesResponse> {
    try {
      const response: AxiosResponse<ListDevicesResponse> = await axios.get(
        `${this.baseURL}/list-devices`,
        {
          params: { ...params },
          headers: {
            Accept: "application/json",
          },
          timeout: 15000,
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error listing devices:", error);
      throw error;
    }
  }

  /**
   * Get device statistics (requires admin key)
   */
  async getDeviceStats(): Promise<DeviceStatsResponse> {
    try {
      const response: AxiosResponse<DeviceStatsResponse> = await axios.get(
        `${this.baseURL}/device-stats`,
        {
          headers: {
            Accept: "application/json",
          },
          timeout: 10000,
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error getting device stats:", error);
      throw error;
    }
  }

  /**
   * Update device information (requires admin key)
   */
  async updateDevice(data: UpdateDeviceRequest): Promise<Device> {
    try {
      const requestData = { ...data };

      const response: AxiosResponse<{ device: Device }> = await axios.put(
        `${this.baseURL}/update-device`,
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          timeout: 10000,
        }
      );

      return response.data.device;
    } catch (error) {
      console.error("Error updating device:", error);
      throw error;
    }
  }

  /**
   * Delete device (requires admin key)
   */
  async deleteDevice(data: DeleteDeviceRequest): Promise<boolean> {
    try {
      const requestData = { ...data };

      await axios.delete(`${this.baseURL}/delete-device`, {
        data: requestData,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        timeout: 10000,
      });

      return true;
    } catch (error) {
      console.error("Error deleting device:", error);
      throw error;
    }
  }

  /**
   * Test device connection
   */
  async testConnection(
    data: GetDeviceDataRequest
  ): Promise<GetDeviceDataResponse> {
    return this.getDeviceData(data);
  }

  /**
   * Validate device credentials
   */
  async validateCredentials(id: number, password: string): Promise<boolean> {
    try {
      await this.getDeviceData({ id, password });
      return true;
    } catch (error) {
      if (error instanceof DeviceApiError && error.status === 400) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Reset all devices password (requires admin key)
   */
  async resetAllPasswords(
    data: ResetAllPasswordsRequest
  ): Promise<ResetAllPasswordsResponse> {
    try {
      const requestData = { ...data };

      const response: AxiosResponse<ResetAllPasswordsResponse> =
        await axios.post(`${this.baseURL}/reset-all-passwords`, requestData, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          timeout: 15000, // Longer timeout for bulk operation
        });

      return response.data;
    } catch (error) {
      console.error("Error resetting all passwords:", error);
      throw error;
    }
  }

  /**
   * Reset specific device password (requires admin key)
   */
  async resetDevicePassword(
    data: ResetDevicePasswordRequest
  ): Promise<ResetDevicePasswordResponse> {
    try {
      const requestData = { ...data };

      const response: AxiosResponse<ResetDevicePasswordResponse> =
        await axios.post(`${this.baseURL}/reset-device-password`, requestData, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          timeout: 10000,
        });

      return response.data;
    } catch (error) {
      console.error("Error resetting device password:", error);
      throw error;
    }
  }

  /**
   * Reset bird count for a device (owner/admin). Default clears TODAY only;
   * pass all=true to wipe ALL counting history (e.g. discard test data).
   */
  async resetBirdCount(
    deviceId: number,
    all = false
  ): Promise<{ message: string; deleted?: number }> {
    const response: AxiosResponse<{ message: string; deleted?: number }> =
      await axios.post(
        `${this.baseURL}/reset-bird-count`,
        { device_id: deviceId, all },
        { headers: { "Content-Type": "application/json", Accept: "application/json" }, timeout: 10000 }
      );
    return response.data;
  }

  /**
   * Get device config (Firebase-authenticated; owner or admin).
   */
  async getDeviceConfig(
    deviceId: number
  ): Promise<Record<string, any> | null> {
    try {
      const response: AxiosResponse<{
        device_id: number;
        config: Record<string, any> | null;
      }> = await axios.get(`${this.baseURL}/device-config`, {
        params: { device_id: deviceId },
        headers: { Accept: "application/json" },
        timeout: 10000,
      });
      return response.data.config;
    } catch (error) {
      if (error instanceof DeviceApiError && error.status === 404) return null;
      console.error("Error getting device config:", error);
      throw error;
    }
  }

  /**
   * Save device config (admin-authenticated)
   */
  async updateDeviceConfig(
    deviceId: number,
    config: Record<string, any>
  ): Promise<void> {
    try {
      await axios.post(
        `${this.baseURL}/device-config`,
        { device_id: deviceId, config },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          timeout: 10000,
        }
      );
    } catch (error) {
      console.error("Error updating device config:", error);
      throw error;
    }
  }

  /**
   * Get bird count statistics per device (admin only)
   */
  async getBirdStats(
    timeRange: "day" | "week" | "month" | "year" = "month",
    deviceId?: number,
    version: "v1" | "v2" | "all" = "all"
  ): Promise<BirdStatsResponse> {
    try {
      const params: Record<string, any> = {
        time_range: timeRange,
        version,
      };
      if (deviceId !== undefined) params.device_id = deviceId;

      const response: AxiosResponse<BirdStatsResponse> = await axios.get(
        `${this.baseURL}/bird-stats`,
        {
          params,
          headers: { Accept: "application/json" },
          timeout: 15000,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error getting bird stats:", error);
      throw error;
    }
  }

  /**
   * Assign (or clear) the owner of a device (admin only).
   * Pass owner_email to assign by email, or neither field to unassign.
   */
  async assignOwner(data: {
    device_id: number;
    owner_email?: string;
    owner_uid?: string;
  }): Promise<{ message: string; device: Record<string, any> }> {
    const response = await axios.post(`${this.baseURL}/assign-owner`, data, {
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      timeout: 10000,
    });
    return response.data;
  }

  /**
   * Share a device with a specific user by email (owner or admin).
   */
  async shareDevice(
    deviceId: number,
    email: string
  ): Promise<{ message: string; share: { uid: string; email: string | null; name: string | null } }> {
    const response = await axios.post(
      `${this.baseURL}/share-device`,
      { device_id: deviceId, email },
      { headers: { "Content-Type": "application/json", Accept: "application/json" }, timeout: 10000 }
    );
    return response.data;
  }

  /**
   * Revoke a user's shared access to a device (owner or admin).
   */
  async unshareDevice(deviceId: number, uid: string): Promise<{ message: string }> {
    const response = await axios.post(
      `${this.baseURL}/unshare-device`,
      { device_id: deviceId, uid },
      { headers: { "Content-Type": "application/json", Accept: "application/json" }, timeout: 10000 }
    );
    return response.data;
  }

  /**
   * Request a Tuya pairing token (to encode into the QR the camera scans).
   * Returns { token, region, secret, expire_time }.
   */
  async getPairingToken(
    pairingType = "EZ"
  ): Promise<{
    token: string;
    region?: string;
    secret?: string;
    expire_time?: number;
  }> {
    const response = await axios.post(
      `${this.baseURL}/tuya/pairing-token`,
      { pairing_type: pairingType },
      { headers: { "Content-Type": "application/json", Accept: "application/json" }, timeout: 15000 }
    );
    return response.data;
  }

  /**
   * Poll for cameras that just paired against [token] (auto-bound server-side,
   * owned by the current user). Returns the discovered devices.
   */
  async discoverDevices(token: string): Promise<
    Array<{
      id: number;
      name: string | null;
      stream_key?: string;
      is_new?: boolean;
      device_password?: string;
    }>
  > {
    const response = await axios.post(
      `${this.baseURL}/tuya/discover`,
      { token },
      { headers: { "Content-Type": "application/json", Accept: "application/json" }, timeout: 15000 }
    );
    return response.data?.data ?? [];
  }

  /**
   * Toggle the AI counter overlay (Contabo CV pipeline) for a camera.
   * When disabled the camera streams directly from Tuya (raw feed, no overlay).
   * Owner or admin only.
   */
  async setCvEnabled(
    deviceId: number,
    enabled: boolean
  ): Promise<{ message: string; device_id: number; cv_enabled: boolean }> {
    const response = await axios.post(
      `${this.baseURL}/device-cv`,
      { device_id: deviceId, enabled },
      { headers: { "Content-Type": "application/json", Accept: "application/json" }, timeout: 15000 }
    );
    return response.data;
  }

  /**
   * Master connect/disconnect for a camera. When disabled the camera does NOT
   * stream at all (Contabo worker idles, no Tuya allocate calls) — saves quota.
   * Owner or admin only.
   */
  async setStreamEnabled(
    deviceId: number,
    enabled: boolean
  ): Promise<{ message: string; device_id: number; stream_enabled: boolean }> {
    const response = await axios.post(
      `${this.baseURL}/device-streaming`,
      { device_id: deviceId, enabled },
      { headers: { "Content-Type": "application/json", Accept: "application/json" }, timeout: 15000 }
    );
    return response.data;
  }

  /**
   * Allocate a fresh direct Tuya live-stream URL for the player (used when the
   * AI overlay is OFF). Tuya URLs are short-lived, so call this again on expiry.
   * Access: admin / owner / shared user.
   */
  async getLiveStreamUrl(
    deviceId: number,
    type: "HLS" | "RTSP" = "HLS"
  ): Promise<{ device_id: number; url: string; type: string }> {
    const response = await axios.post(
      `${this.baseURL}/tuya/live-url`,
      { device_id: deviceId, type },
      { headers: { "Content-Type": "application/json", Accept: "application/json" }, timeout: 20000 }
    );
    return response.data;
  }

  /**
   * Wake-on-view: tell the backend a live-view is open so a headless camera's
   * CV worker spins up the ffmpeg encode within a few seconds (the overlay HLS
   * only exists while a viewer is present). Call on stream open + on a ~25s
   * heartbeat while watching. Best-effort — never throws, so it can't break
   * playback if the endpoint is briefly unreachable.
   */
  async pingLiveView(deviceId: number): Promise<void> {
    if (!deviceId || deviceId <= 0) return;
    try {
      await axios.post(
        `${this.baseURL}/tuya/live-view`,
        { device_id: deviceId },
        { headers: { "Content-Type": "application/json", Accept: "application/json" }, timeout: 8000 }
      );
    } catch (_) {
      // best-effort; ignore (a missed heartbeat self-heals on the next tick)
    }
  }

  /**
   * List counting-quality reports submitted by users. Admin sees all reports;
   * owners see only their own. Optional filters by status and/or device_id.
   */
  async getReports(
    params: { status?: string; device_id?: number } = {}
  ): Promise<IotReport[]> {
    const response: AxiosResponse<{ data: IotReport[] }> = await axios.get(
      `${this.baseURL}/reports`,
      {
        params: { ...params },
        headers: { Accept: "application/json" },
        timeout: 15000,
      }
    );
    return response.data.data;
  }

  /**
   * Update a report's status (+ optional admin note). Admin only (403 for
   * non-admins). Returns the updated report.
   */
  async updateReportStatus(
    id: number,
    status: string,
    adminNote?: string
  ): Promise<{ message: string; report: IotReport }> {
    const response: AxiosResponse<{ message: string; report: IotReport }> =
      await axios.post(
        `${this.baseURL}/report/${id}/status`,
        { status, admin_note: adminNote },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          timeout: 10000,
        }
      );
    return response.data;
  }

  /**
   * Whether a Firebase user is signed in (server access is via their ID token).
   * Kept for backwards-compatibility with callers that gated "server mode".
   */
  hasAdminKey(): boolean {
    return !!auth.currentUser;
  }

  /**
   * Wait for Firebase to finish restoring the session, then report whether a
   * user is signed in. Use this (instead of the synchronous `hasAdminKey`) on
   * page load / before fetching, so we never fall back to stale local data
   * while the auth session is still being restored.
   */
  async ensureAuthReady(): Promise<boolean> {
    await authReady();
    return !!auth.currentUser;
  }
}

// Create and export a singleton instance
export const deviceApiService = new DeviceApiService();

// Export the service class for testing or multiple instances
export default DeviceApiService;
